import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  DocumentChartBarIcon, FunnelIcon, MagnifyingGlassIcon,
  ArrowPathIcon, ExclamationCircleIcon, PlusIcon, XMarkIcon,
} from '@heroicons/react/24/outline';
import { ReportService } from '../../../shared/services/reportService';
import ReportStatusBadge from '../components/ReportStatusBadge';
import ReportTypeBadge from '../components/ReportTypeBadge';
import ReportActionsMenu from '../components/ReportActionsMenu';
import GenerateReportModal from '../components/GenerateReportModal';
import { REPORT_TYPE_OPTIONS, STATUS_OPTIONS } from '../config/reportsConfig';

const PAGE_SIZE = 20;
const POLL_INTERVAL = 10000;

const FORMAT_BADGE = {
  CSV:   'bg-sky-100 text-sky-700 border border-sky-200',
  EXCEL: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
  PDF:   'bg-rose-100 text-rose-700 border border-rose-200',
};

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const formatDateRange = (start, end) => {
  if (!start && !end) return '—';
  return `${formatDate(start)} → ${formatDate(end)}`;
};

const truncateId = (id) => (id ? String(id).slice(0, 8) + '…' : '—');

const StatCard = ({ label, value, colorClass, loading }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 flex items-center gap-4">
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-white text-sm font-bold ${colorClass}`}>
      {loading ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : value}
    </div>
    <span className="text-sm text-gray-600 dark:text-gray-400">{label}</span>
  </div>
);

const Toast = ({ message, type, onDismiss }) => {
  const cls = type === 'error'
    ? 'bg-red-500'
    : 'bg-green-500';
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-white text-sm ${cls}`}>
      {message}
      <button onClick={onDismiss}><XMarkIcon className="w-4 h-4" /></button>
    </div>
  );
};

export default function ReportsPage() {
  const [reports, setReports] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [toast, setToast] = useState(null);

  const [filters, setFilters] = useState({ search: '', reportType: '', status: '', from: '', to: '' });
  const [appliedFilters, setAppliedFilters] = useState(filters);

  const searchDebounceRef = useRef(null);
  const pollRef = useRef(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchReports = useCallback(async (page, activeFilters, silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const params = { page, limit: PAGE_SIZE, ...activeFilters };
      Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
      const res = await ReportService.list(params);
      const data = res.data?.data ?? res.data ?? {};
      setReports(data.reports ?? data.data ?? []);
      setPagination(data.pagination ?? { page, limit: PAGE_SIZE, total: 0, totalPages: 1 });

      // Derive stats from list response if no separate stats endpoint
      if (data.stats) setStats(data.stats);
    } catch (err) {
      if (!silent) setError(err?.response?.data?.message ?? 'Failed to load reports.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReports(currentPage, appliedFilters);
  }, [currentPage, appliedFilters, fetchReports]);

  // Poll when any row is PENDING or IN_PROGRESS
  useEffect(() => {
    const hasActive = reports.some((r) => r.status === 'PENDING' || r.status === 'IN_PROGRESS');
    clearInterval(pollRef.current);
    if (hasActive) {
      pollRef.current = setInterval(() => {
        fetchReports(currentPage, appliedFilters, true);
      }, POLL_INTERVAL);
    }
    return () => clearInterval(pollRef.current);
  }, [reports, currentPage, appliedFilters, fetchReports]);

  const handleSearchChange = (value) => {
    setFilters((f) => ({ ...f, search: value }));
    clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setCurrentPage(1);
      setAppliedFilters((f) => ({ ...f, search: value }));
    }, 400);
  };

  const applyFilters = () => {
    setCurrentPage(1);
    setAppliedFilters({ ...filters });
    setShowFilters(false);
  };

  const clearFilters = () => {
    const empty = { search: '', reportType: '', status: '', from: '', to: '' };
    setFilters(empty);
    setAppliedFilters(empty);
    setCurrentPage(1);
    setShowFilters(false);
  };

  const activeFilterCount = [appliedFilters.reportType, appliedFilters.status, appliedFilters.from, appliedFilters.to].filter(Boolean).length;

  const handleDeleted = (id) => setReports((prev) => prev.filter((r) => r.id !== id));

  const handleRegenerated = () => fetchReports(currentPage, appliedFilters);

  const handleModalSuccess = () => {
    setShowModal(false);
    showToast("Report queued. You'll receive an email when ready.", "success");
    fetchReports(currentPage, appliedFilters);
  };

  // Derive stats from reports list as fallback
  const computedStats = stats ?? {
    total: pagination.total,
    generated: reports.filter((r) => r.status === 'GENERATED').length,
    failed: reports.filter((r) => r.status === 'FAILED').length,
    inProgress: reports.filter((r) => r.status === 'IN_PROGRESS' || r.status === 'PENDING').length,
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-lg bg-[#4057fe] flex items-center justify-center">
              <DocumentChartBarIcon className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Vaccine Inventory Reports</h1>
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 ml-12">Generate and manage inventory reports</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 text-sm px-4 py-2 rounded-lg bg-[#4057fe] text-white hover:bg-[#3347e0] whitespace-nowrap"
        >
          <PlusIcon className="w-4 h-4" />
          Generate Report
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatCard label="Total Reports" value={computedStats.total ?? '—'} colorClass="bg-[#4057fe]" loading={loading} />
        <StatCard label="Generated" value={computedStats.generated ?? '—'} colorClass="bg-green-500" loading={loading} />
        <StatCard label="Failed" value={computedStats.failed ?? '—'} colorClass="bg-red-500" loading={loading} />
        <StatCard label="In Progress" value={computedStats.inProgress ?? '—'} colorClass="bg-yellow-500" loading={loading} />
      </div>

      {/* Toolbar */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4 mb-4">
        <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
          {/* Search */}
          <div className="relative flex-1 max-w-sm">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={filters.search}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Search by report ID…"
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4057fe]/30 focus:border-[#4057fe]"
            />
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {/* Quick report type filter */}
            <select
              value={appliedFilters.reportType}
              onChange={(e) => { setAppliedFilters((f) => ({ ...f, reportType: e.target.value })); setCurrentPage(1); }}
              className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#4057fe]/30"
            >
              <option value="">All Types</option>
              {REPORT_TYPE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>

            {/* Quick status filter */}
            <select
              value={appliedFilters.status}
              onChange={(e) => { setAppliedFilters((f) => ({ ...f, status: e.target.value })); setCurrentPage(1); }}
              className="text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#4057fe]/30"
            >
              <option value="">All Statuses</option>
              {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>

            {/* Advanced filters toggle */}
            <button
              onClick={() => setShowFilters((v) => !v)}
              className={`relative flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg border transition-colors ${showFilters || activeFilterCount > 0 ? 'bg-[#4057fe] text-white border-[#4057fe]' : 'bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-200 border-gray-200 dark:border-gray-600 hover:bg-gray-50'}`}
            >
              <FunnelIcon className="w-4 h-4" />
              Filters
              {activeFilterCount > 0 && (
                <span className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center">{activeFilterCount}</span>
              )}
            </button>

            {/* Refresh */}
            <button
              onClick={() => fetchReports(currentPage, appliedFilters)}
              className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
              title="Refresh"
            >
              <ArrowPathIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>

        {/* Advanced filter panel */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Created From</label>
              <input
                type="date"
                value={filters.from}
                onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
                className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#4057fe]/30"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Created To</label>
              <input
                type="date"
                value={filters.to}
                onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
                className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#4057fe]/30"
              />
            </div>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <button onClick={clearFilters} className="text-sm px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                Clear all
              </button>
              <button onClick={applyFilters} className="text-sm px-4 py-2 rounded-lg bg-[#4057fe] text-white hover:bg-[#3347e0]">
                Apply filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {error ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-red-500">
            <ExclamationCircleIcon className="w-10 h-10" />
            <p className="text-sm font-medium">{error}</p>
            <button onClick={() => fetchReports(currentPage, appliedFilters)} className="text-sm underline">Retry</button>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#e9f1ff] dark:bg-gray-700/60">
                  <tr>
                    {['Report ID', 'Type', 'Scope', 'Date Range', 'Format', 'Status', 'Generated By', 'Created At', ''].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {loading ? (
                    Array.from({ length: 8 }).map((_, i) => (
                      <tr key={i} className="animate-pulse">
                        {Array.from({ length: 9 }).map((__, j) => (
                          <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" /></td>
                        ))}
                      </tr>
                    ))
                  ) : reports.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center py-16 text-gray-400 dark:text-gray-500">
                        <DocumentChartBarIcon className="w-10 h-10 mx-auto mb-2 opacity-30" />
                        <p className="text-sm">No reports found</p>
                        <button onClick={() => setShowModal(true)} className="mt-2 text-sm text-[#4057fe] hover:underline">
                          Generate your first report
                        </button>
                      </td>
                    </tr>
                  ) : reports.map((report) => (
                    <tr key={report.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors">
                      <td className="px-4 py-3 font-mono text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap" title={report.id}>
                        {truncateId(report.id)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <ReportTypeBadge type={report.reportType} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-gray-900 dark:text-white text-sm">{report.scopeLevel ?? '—'}</div>
                        <div className="text-xs text-gray-400">{report.scopeName ?? report.scopeId ?? ''}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-300 text-xs">
                        {formatDateRange(report.filters?.startDate ?? report.startDate, report.filters?.endDate ?? report.endDate)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${FORMAT_BADGE[report.fileFormat] ?? 'bg-gray-100 text-gray-600'}`}>
                          {report.fileFormat ?? '—'}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <ReportStatusBadge status={report.status} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-gray-700 dark:text-gray-300 text-sm">{report.generatedBy?.name ?? report.createdByName ?? '—'}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-600 dark:text-gray-300 text-xs">
                        {formatDate(report.createdAt)}
                      </td>
                      <td className="px-4 py-3">
                        <ReportActionsMenu
                          report={report}
                          onDeleted={handleDeleted}
                          onRegenerated={handleRegenerated}
                          onToast={showToast}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Showing {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, pagination.total)} of {pagination.total}
                </p>
                <div className="flex items-center gap-1">
                  <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((p) => p - 1)}
                    className="px-3 py-1.5 text-xs rounded border border-gray-200 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
                  >
                    Previous
                  </button>
                  {Array.from({ length: Math.min(pagination.totalPages, 7) }, (_, i) => {
                    const page = pagination.totalPages <= 7 ? i + 1 : currentPage <= 4 ? i + 1 : currentPage + i - 3;
                    if (page < 1 || page > pagination.totalPages) return null;
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-3 py-1.5 text-xs rounded border transition-colors ${page === currentPage ? 'bg-[#4057fe] text-white border-[#4057fe]' : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    disabled={currentPage === pagination.totalPages}
                    onClick={() => setCurrentPage((p) => p + 1)}
                    className="px-3 py-1.5 text-xs rounded border border-gray-200 dark:border-gray-600 disabled:opacity-40 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Generate modal */}
      {showModal && (
        <GenerateReportModal onClose={() => setShowModal(false)} onSuccess={handleModalSuccess} />
      )}

      {/* Toast */}
      {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
    </div>
  );
}
